import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createTaskSchedule,
  deleteTaskSchedule,
  fetchTaskScheduleById,
  RequestItem,
  TaskSchedule,
  updateTaskSchedule,
} from "../../../api/requests";
import { computeScheduleRunsInMonth, parseScheduleRunLocalParts } from "../../../utils/scheduleRunsInMonth";
import { getRunsCalendarCells } from "../requestDetailUtils";
import type { CheckDetailKind } from "../checkDetailKind";
import {
  buildSchedulePayload,
  scheduleFormFromItem,
  type ScheduleFormState,
} from "./scheduleFormState";

type Notify = (options: { message: string; variant: "success" | "error" | "info" }) => void;

interface UseRequestScheduleOptions {
  kind?: CheckDetailKind;
  data: RequestItem | null;
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<RequestItem | null>>;
  showNotification: Notify;
  closeActionsMenu: () => void;
  canScanBySchedule: boolean;
}

export const useRequestSchedule = ({
  kind = "site",
  data,
  loading,
  setData,
  showNotification,
  closeActionsMenu,
  canScanBySchedule,
}: UseRequestScheduleOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const autoScheduleSetupHandledRef = useRef(false);

  const [scheduleItem, setScheduleItem] = useState<TaskSchedule | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleDeleting, setScheduleDeleting] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>(scheduleFormFromItem(null));

  const [isRunsCalendarOpen, setIsRunsCalendarOpen] = useState(false);
  const [runsCalYm, setRunsCalYm] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });
  const [runsCalSelectedDay, setRunsCalSelectedDay] = useState<number | null>(null);

  useEffect(() => {
    setScheduleItem(data?.schedule?.[0] ?? null);
  }, [data?.id, data?.schedule]);

  const applyScheduleForm = useCallback((item: TaskSchedule | null) => {
    setScheduleItem(item);
    setScheduleForm(scheduleFormFromItem(item));
  }, []);

  const handleOpenScheduleModal = useCallback(async () => {
    if (!data || !canScanBySchedule) return;
    closeActionsMenu();
    setIsScheduleModalOpen(true);
    const existingId = scheduleItem?.id ?? data.schedule?.[0]?.id;
    if (!existingId) {
      applyScheduleForm(null);
      return;
    }
    setScheduleLoading(true);
    try {
      const fetched = await fetchTaskScheduleById(existingId);
      applyScheduleForm(fetched);
      setData((prev) => (prev ? { ...prev, schedule: [fetched] } : prev));
    } catch (err: unknown) {
    } finally {
      setScheduleLoading(false);
    }
  }, [
    applyScheduleForm,
    canScanBySchedule,
    closeActionsMenu,
    data,
    scheduleItem,
    setData,
    showNotification,
  ]);

  const handleCloseScheduleModal = useCallback(() => {
    if (scheduleSaving || scheduleDeleting) return;
    setIsScheduleModalOpen(false);
  }, [scheduleDeleting, scheduleSaving]);

  const handleOpenRunsCalendar = useCallback(() => {
    if (!scheduleItem?.id) return;
    const base = scheduleItem.nextRunAt ? new Date(scheduleItem.nextRunAt) : new Date();
    setRunsCalYm({ y: base.getFullYear(), m: base.getMonth() });
    setRunsCalSelectedDay(null);
    setIsRunsCalendarOpen(true);
  }, [scheduleItem]);

  const handleCloseRunsCalendar = useCallback(() => {
    setIsRunsCalendarOpen(false);
  }, []);

  const shiftRunsCalMonth = useCallback((delta: number) => {
    setRunsCalYm((prev) => {
      const d = new Date(prev.y, prev.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
    setRunsCalSelectedDay(null);
  }, []);

  const handleSaveSchedule = useCallback(async () => {
    if (!data) return;
    const payload = buildSchedulePayload(data.id, scheduleForm, kind);
    setScheduleSaving(true);
    try {
      const saved = scheduleItem?.id
        ? await updateTaskSchedule(scheduleItem.id, payload)
        : await createTaskSchedule(payload);
      applyScheduleForm(saved);
      setData((prev) => (prev ? { ...prev, schedule: [saved] } : prev));
      showNotification({
        message: scheduleItem?.id ? "Расписание обновлено" : "Расписание создано",
        variant: "success",
      });
      setIsScheduleModalOpen(false);
    } catch (err: unknown) {
    } finally {
      setScheduleSaving(false);
    }
  }, [applyScheduleForm, data, kind, scheduleForm, scheduleItem, setData, showNotification]);

  const handleDeleteSchedule = useCallback(async () => {
    if (!scheduleItem?.id) return;
    setScheduleDeleting(true);
    try {
      await deleteTaskSchedule(scheduleItem.id);
      applyScheduleForm(null);
      setData((prev) => (prev ? { ...prev, schedule: [] } : prev));
      showNotification({ message: "Расписание удалено", variant: "success" });
      setIsScheduleModalOpen(false);
    } catch (err: unknown) {
    } finally {
      setScheduleDeleting(false);
    }
  }, [applyScheduleForm, scheduleItem, setData, showNotification]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenScheduleSetup = params.get("openScheduleSetup") === "1";
    if (
      !shouldOpenScheduleSetup ||
      autoScheduleSetupHandledRef.current ||
      loading ||
      !data ||
      !canScanBySchedule
    ) {
      return;
    }
    autoScheduleSetupHandledRef.current = true;
    handleOpenScheduleModal();
    params.delete("openScheduleSetup");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [
    canScanBySchedule,
    data,
    handleOpenScheduleModal,
    loading,
    location.pathname,
    location.search,
    navigate,
  ]);

  const runsCalItems = useMemo(() => {
    if (!scheduleItem?.schedule) return [];
    return computeScheduleRunsInMonth(scheduleItem, runsCalYm.y, runsCalYm.m);
  }, [scheduleItem, runsCalYm.y, runsCalYm.m]);

  const runsCalTitle = useMemo(() => {
    const d = new Date(runsCalYm.y, runsCalYm.m, 1);
    return d.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
  }, [runsCalYm.y, runsCalYm.m]);

  const runsCalCells = useMemo(
    () => getRunsCalendarCells(runsCalYm.y, runsCalYm.m),
    [runsCalYm.y, runsCalYm.m]
  );

  const runsCalDaysWithRuns = useMemo(() => {
    const set = new Set<number>();
    runsCalItems.forEach((iso) => {
      const parts = parseScheduleRunLocalParts(iso);
      if (parts && parts.y === runsCalYm.y && parts.m === runsCalYm.m) {
        set.add(parts.d);
      }
    });
    return set;
  }, [runsCalItems, runsCalYm.y, runsCalYm.m]);

  const runsCalList = useMemo(() => {
    const sorted = [...runsCalItems]
      .filter((iso) => {
        const parts = parseScheduleRunLocalParts(iso);
        return Boolean(parts && parts.y === runsCalYm.y && parts.m === runsCalYm.m);
      })
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    if (runsCalSelectedDay === null) return sorted;
    return sorted.filter((iso) => parseScheduleRunLocalParts(iso)?.d === runsCalSelectedDay);
  }, [runsCalItems, runsCalYm.y, runsCalYm.m, runsCalSelectedDay]);

  const hasSchedule = Boolean(scheduleItem?.id);

  const setSchedulePeriod = useCallback((value: SetStateAction<ScheduleFormState["period"]>) => {
    setScheduleForm((prev) => ({
      ...prev,
      period: typeof value === "function" ? value(prev.period) : value,
    }));
  }, []);

  const setScheduleWeekDay = useCallback((value: SetStateAction<number>) => {
    setScheduleForm((prev) => ({
      ...prev,
      weekDay: typeof value === "function" ? value(prev.weekDay) : value,
    }));
  }, []);

  const setScheduleMonthDay = useCallback((value: SetStateAction<number>) => {
    setScheduleForm((prev) => ({
      ...prev,
      monthDay: typeof value === "function" ? value(prev.monthDay) : value,
    }));
  }, []);

  const setScheduleMonthLastDay = useCallback((value: SetStateAction<boolean>) => {
    setScheduleForm((prev) => ({
      ...prev,
      monthLastDay: typeof value === "function" ? value(prev.monthLastDay) : value,
    }));
  }, []);

  return {
    scheduleItem,
    isScheduleModalOpen,
    scheduleLoading,
    scheduleSaving,
    scheduleDeleting,
    schedulePeriod: scheduleForm.period,
    scheduleWeekDay: scheduleForm.weekDay,
    scheduleMonthDay: scheduleForm.monthDay,
    scheduleMonthLastDay: scheduleForm.monthLastDay,
    isRunsCalendarOpen,
    runsCalYm,
    runsCalSelectedDay,
    hasSchedule,
    runsCalItems,
    runsCalTitle,
    runsCalCells,
    runsCalDaysWithRuns,
    runsCalList,
    runsCalToday: new Date(),
    setSchedulePeriod,
    setScheduleWeekDay,
    setScheduleMonthDay,
    setScheduleMonthLastDay,
    setRunsCalSelectedDay,
    handleOpenScheduleModal,
    handleCloseScheduleModal,
    handleOpenRunsCalendar,
    handleCloseRunsCalendar,
    shiftRunsCalMonth,
    handleSaveSchedule,
    handleDeleteSchedule,
  };
};
